import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDrop } from 'react-dnd';

import { useTranslation } from '@mint-lib/i18n';
import { clone } from 'remeda';

import { Box, Typography } from '@myn/mui';

import { Dimension } from '../../../types/campaignBuilder.js';
import {
  DEPENDENT_COLUMNS,
  DimensionsDragType,
} from '../EditModal.constants.js';
import DimensionRow from './DimensionRow/index.js';

type Props = {
  currentDimensions: Dimension[];
  setCurrentDimensions: Dispatch<SetStateAction<Dimension[]>>;
  className?: string;
  handleDimensionAddition: (dimension: Dimension, index: number) => void;
};

const EditViewColumn = ({
  className,
  currentDimensions,
  setCurrentDimensions = () => null,
  handleDimensionAddition,
}: Props) => {
  const { t } = useTranslation('@myn/planning');

  const [allDimensions, setAllDimensions] = useState<Dimension[]>([]);

  const handleDimensionMove = useCallback(
    (dragIndex: number, dropIndex: number) => {
      const copy = clone(currentDimensions);
      copy.splice(dragIndex, 1);
      copy.splice(dropIndex, 0, currentDimensions[dragIndex]);
      setCurrentDimensions(copy);
    },
    [currentDimensions],
  );

  const handleDimensionRemove = useCallback(
    (dimension: Dimension) => {
      const copy = clone(currentDimensions);
      const newDimensions = copy.filter(
        (item) =>
          !(
            DEPENDENT_COLUMNS[dimension.value]?.includes(item.value) ||
            item.value === dimension.value
          ),
      );
      setCurrentDimensions(newDimensions);
    },
    [currentDimensions],
  );

  const ref = useRef<HTMLDivElement | null>(null);

  const [{ handlerId }, drop] = useDrop({
    accept: [
      DimensionsDragType.DIMENSION_ROW,
      DimensionsDragType.GROUPED_DIMENSION,
    ],
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
      };
    },
  });

  drop(ref);

  useEffect(() => {
    const dimensionsWithMediaField = [
      { value: 'media', name: t('Media') },
      ...currentDimensions,
    ];

    setAllDimensions(dimensionsWithMediaField);
  }, [currentDimensions]);

  return (
    <Box className={className} sx={{ overflow: 'hidden', flexBasis: '50%' }}>
      <Typography variant="h4" sx={{ mb: '16px' }}>
        {t('Added columns')}
      </Typography>
      <div ref={ref} data-handler-id={handlerId}>
        {allDimensions.map((item, index) => (
          <DimensionRow
            key={item.value}
            index={index - 1} // minus default media field index
            dimension={item}
            onDimensionMove={handleDimensionMove}
            onDimensionAddition={handleDimensionAddition}
            onDimensionRemove={handleDimensionRemove}
          />
        ))}
      </div>
    </Box>
  );
};

export default memo(EditViewColumn);
